import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/auth.store';
import { useInventory } from '../../hooks/useInventory';
import { usePosStore } from '../../stores/pos.store';
import CategorySelector from './CategorySelector';
import ProductGrid from './ProductGrid';
import Basket from './Basket';
import Checkout from './Checkout';
import ModuleTiles from './ModuleTiles';
import SearchInput from '../../components/common/SearchInput';
import OfflineBanner from '../../components/common/OfflineBanner';
import type { Inventory } from '@motra/shared';

export default function POSPage() {
  const { t } = useTranslation();
  const account = useAuthStore((s) => s.account);
  const { data: inventory = [], isLoading } = useInventory();
  const addItem = usePosStore((s) => s.addItem);

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  // Parse inventory categories from account config
  const categories = useMemo(() => {
    if (!account?.inv_type) return [];
    try {
      return JSON.parse(account.inv_type) as string[];
    } catch {
      return [];
    }
  }, [account?.inv_type]);

  // Filtered products
  const filtered = useMemo(() => {
    return inventory.filter((item: Inventory) => {
      const matchCategory = !selectedCategory || item.category === selectedCategory;
      const matchSearch =
        !search ||
        item.name?.toLowerCase().includes(search.toLowerCase()) ||
        item.barcode?.includes(search);
      return matchCategory && matchSearch;
    });
  }, [inventory, selectedCategory, search]);

  return (
    <div className="pos-page">
      <OfflineBanner />

      {/* Category column */}
      <div className="pos-categories">
        <CategorySelector
          categories={categories}
          selectedCategory={selectedCategory}
          onSelect={setSelectedCategory}
        />
      </div>

      {/* Products column */}
      <div className="pos-products">
        <ModuleTiles />
        <div className="pos-products__search">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={`${t('search', 'Search')} products, barcode...`}
          />
        </div>
        <ProductGrid
          items={filtered}
          onAddToBasket={addItem}
          loading={isLoading}
        />
      </div>

      {/* Basket column */}
      <div className="pos-basket-column">
        <Basket onCheckout={() => setCheckoutOpen(true)} />
      </div>

      {/* Checkout modal */}
      <Checkout
        open={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
      />
    </div>
  );
}
